// Content for the HUCHTHAUSEN clan heritage site.
// Structure mirrors an editorial clan-heritage layout; all copy is original,
// based on public sources about the Huchthausen family & the Lichtenstein Cave.
// NOTE: figures marked as estimates are MOCKED / illustrative for this teaser.

export const brand = {
  name: "Huchthausen",
  old: "Aus dem Dickicht",
  motto: "Per silvam per saecula",
  mottoEn: "Through the forest, through the ages",
  tagline: "By Forest and By Stone",
};

export const navLinks = [
  { id: "origins", label: "Origins" },
  { id: "name", label: "The Name" },
  { id: "lineage", label: "Lineage" },
  { id: "tree", label: "The Tree" },
  { id: "kin", label: "The Kin" },
  { id: "legacy", label: "Legacy" },
  { id: "map", label: "The Map" },
  { id: "distribution", label: "The World" },
];

export const hero = {
  eyebrow: "The oldest known family on earth",
  titleTop: "By Forest",
  titleBottom: "and By Stone",
  text:
    "The story of the Huchthausens — not a legend but a lineage. One family kept to the same soil beneath the Harz mountains for three thousand years, from the Bronze-Age dark of a hidden cave to the living present.",
  ctaPrimary: "Discover Our Story",
  ctaSecondary: "Trace Your Lineage",
  image:
    "https://images.unsplash.com/photo-1503435980610-a51f3ddfee50?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwzfHxmb2dneSUyMGZvcmVzdHxlbnwwfHx8fDE3ODgwMTUwMDJ8MA&ixlib=rb-4.1.0&q=85&w=1920",
};

export const origins = {
  label: "The Origins",
  kicker: "Familie Huchthausen",
  title: "A kindred forged in stone, soil and silence.",
  cards: [
    {
      n: "01",
      title: "Children of the Cave",
      text:
        "Beneath the Harz lies the Lichtenstein Cave, where the Bronze-Age dead were laid three thousand years ago. From them, by blood unbroken, the Huchthausens descend.",
    },
    {
      n: "02",
      title: "People of the Thicket",
      text:
        "The name itself is the land — Hucht, the dense wild thicket, and hausen, the dwelling. To be a Huchthausen is to be of the woods.",
    },
    {
      n: "03",
      title: "One Soil, Many Lives",
      text:
        "While empires rose and fell, the family stayed. One hundred and twenty-two generations have been born, and buried, within sight of the same hills.",
    },
  ],
};

export const name = {
  label: "The Name",
  title: "Hucht · Hausen",
  intro:
    "The name was not chosen but grown — two old German words that together describe a people and the place that made them.",
  parts: [
    {
      word: "Hucht",
      gloss: "thicket · brushwood",
      text:
        "From an old Low-German word for dense, tangled undergrowth — the wild woods of the Harz.",
    },
    {
      word: "-hausen",
      gloss: "houses · dwelling",
      text:
        "From Old High German hūs, ‘house’ — the ending of countless German place-names, meaning a settlement.",
    },
  ],
  combined: {
    word: "Huchthausen",
    gloss: "“the dwelling by the thicket”",
    pron: "HOOKHT · how · zen",
  },
  kindred:
    "From the same root grew Huchtmann, Huchting and Huchtkötter — cousins of the thicket.",
};

export const lineage = {
  label: "The Lineage",
  title: "Three thousand years, one unbroken thread.",
  image: "/hez/old-entrance.jpg",
  events: [
    {
      year: "c. 1000 BC",
      title: "Laid in the Cave",
      text: "In the Bronze Age the dead are placed in the Lichtenstein Cave. Their blood will outlast empires.",
    },
    {
      year: "Middle Ages",
      title: "A Name is Written",
      text: "The settlement by the thicket is recorded; the family takes the name Huchthausen.",
    },
    {
      year: "1937",
      title: "The Cave is Found",
      text: "Deep in the Harz, the Lichtenstein Cave and its ancient bones are rediscovered.",
    },
    {
      year: "2008",
      title: "The Blood Speaks",
      text: "DNA proves that living villagers of Förste descend directly from the cave's dead — 122 generations.",
    },
    {
      year: "Today",
      title: "Still of the Soil",
      text: "The Huchthausens remain where they began — the longest-documented family line on earth.",
    },
  ],
};

export const familyTree = {
  label: "The Tree",
  title: "One root, a hundred and twenty-two rings.",
  intro:
    "From the Bronze-Age dead of the Lichtenstein Cave, the line branches down through three thousand years to the living kin. Follow a branch to trace it.",
  root: {
    id: "n1",
    gen: "Gen. 1",
    era: "c. 1000 BC",
    title: "The Cave Ancestors",
    note:
      "The Bronze-Age dead laid in the Lichtenstein Cave — the single root from which every Huchthausen descends, proven by DNA three thousand years later.",
    children: [
      {
        id: "n2",
        gen: "Gen. ~40",
        era: "Iron Age – Early Middle Ages",
        title: "The Forest Dwellers",
        note:
          "Unnamed generations who kept to the thicket of the Harz — hunting, herding and burying their dead within sight of the cave.",
        children: [
          {
            id: "n3",
            gen: "Gen. ~80",
            era: "Middle Ages",
            title: "House of Huchthausen",
            note:
              "The settlement by the thicket is written down at last, and the family takes the name it still carries.",
            children: [
              {
                id: "n4",
                gen: "Gen. ~110",
                era: "16th – 19th century",
                title: "The Harz Line",
                note:
                  "The stem that never left — farmers and villagers of Förste, rooted to the same soil for centuries.",
                children: [
                  {
                    id: "n5",
                    gen: "Gen. 122",
                    era: "present day",
                    title: "Manfred Huchthausen",
                    epithet: "The Living Proof",
                    note:
                      "The man whose DNA closed the circle, binding the living family to the cave's dead across 122 generations.",
                    kin: true,
                  },
                ],
              },
              {
                id: "n6",
                gen: "Gen. ~110",
                era: "18th – 20th century",
                title: "The Wandering Line",
                note:
                  "Branches that crossed the sea, carrying the name to America and the wider world.",
                children: [
                  {
                    id: "n7",
                    gen: "Gen. ~118",
                    era: "1904 – 1945",
                    title: "Walter J. Huchthausen",
                    epithet: "The Guardian",
                    note: "A Monuments Man who gave his life protecting Europe's art in the war.",
                    kin: true,
                  },
                  {
                    id: "n8",
                    gen: "Gen. ~119",
                    era: "1939 – 2008",
                    title: "Peter A. Huchthausen",
                    epithet: "The Mariner",
                    note: "A captain of the U.S. Navy and author who wrote of the deep.",
                    kin: true,
                  },
                  {
                    id: "n9",
                    gen: "Gen. ~120",
                    era: "born 1951",
                    title: "David R. Huchthausen",
                    epithet: "The Glassmaker",
                    note: "An artist in glass held in the great museums of the world.",
                    kin: true,
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
};


export const kin = {
  label: "The Kin",
  title: "Faces of the line.",
  intro:
    "From three thousand years came few names the wider world would know — yet each carried the thicket in their blood. Choose one to follow their story.",
  people: [
    {
      id: "k1",
      name: "Manfred Huchthausen",
      epithet: "The Living Proof",
      place: "Förste · Harz",
      era: "born 1950",
      text:
        "A man of Förste whose DNA closed a circle three thousand years wide, binding him directly to the Bronze-Age dead of the Lichtenstein Cave — living proof of 122 generations in one place.",
      image: "/people/manfred2.jpg",
    },
    {
      id: "k2",
      name: "Peter A. Huchthausen",
      epithet: "The Mariner",
      place: "United States",
      era: "1939 – 2008",
      text:
        "A captain of the United States Navy and a noted author, who wrote of submarines, the Cold War and the deep — carrying the name across the world's oceans.",
      image: "/people/peter-wide.jpg",
    },
    {
      id: "k3",
      name: "David R. Huchthausen",
      epithet: "The Glassmaker",
      place: "Seattle · USA",
      era: "born 1951",
      text:
        "An American artist in glass whose luminous sculptures are held in the great museums and galleries of the world.",
      image: "/people/david-wide.jpg",
    },
    {
      id: "k4",
      name: "Walter J. Huchthausen",
      epithet: "The Guardian",
      place: "United States",
      era: "1904 – 1945",
      text:
        "A ‘Monuments Man’ and captain of the U.S. Army who gave his life protecting Europe's art in the last months of the war. Awarded the Purple Heart.",
      image: "/people/walter-wide.jpg",
    },
    {
      id: "k5",
      name: "Volker Huchthausen",
      epithet: "The Next Generation",
      place: "Förste · Harz",
      era: "born 1989",
      text:
        "Son of Manfred, and the next link in the chain — the youngest keeper of a bloodline traced by DNA to the Bronze-Age dead of the Lichtenstein Cave. He chairs the Kulturschmiede Osterode e.V., staging music and culture across the Harz.",
      image: "/people/volker1.jpg",
    },
  ],
};

export const legacy = {
  label: "The Legacy",
  title: "Land that remembers.",
  intro: "The woods, stone and hidden dark that carry our name.",
  items: [
    {
      caption: "The thicket",
      image:
        "https://images.unsplash.com/photo-1708927764458-92cce71d0146?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwzfHxhbmNpZW50JTIwZm9yZXN0fGVufDB8fHx8MTc4ODAxNDU1MXww&ixlib=rb-4.1.0&q=85&w=1000",
      span: "lg:col-span-2",
    },
    {
      caption: "The hidden cave",
      image: "/cave/entrance-2013.jpg",
      span: "",
    },
    {
      caption: "The Harz hills",
      image: "/cave/hill.jpg",
      span: "",
    },
    {
      caption: "The old valley",
      image:
        "https://images.unsplash.com/photo-1568097299894-7da224cd4930?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwzfHxIYXJ6JTIwbW91bnRhaW5zfGVufDB8fHx8MTc4ODAxNDU1MXww&ixlib=rb-4.1.0&q=85&w=1000",
      span: "lg:col-span-2",
    },
  ],
};

export const roots = {
  label: "The Roots",
  title: "Bound to the ground.",
  text:
    "No woven cloth marks the Huchthausens, but the land itself — the black soil of the Harz, the green of the thicket, the grey stone of the cave. Their colours are the earth's, and their pattern is time.",
  image: "/forste/forest2.jpg",
};

export const village = {
  eyebrow: "The Village",
  title: "Förste, beneath the Harz.",
  intro:
    "The small Lower-Saxon village of Förste, below the Lichtenstein hill, is where the family's story has stayed put for three thousand years. A few pictures of the place — its woods, its water, and its everyday life.",
  lead: {
    src: "/forste/aerial.jpg",
    caption: "Förste from above — red roofs among the fields and wooded hills of the Harz.",
  },
  images: [
    { src: "/forste/creek.jpg", caption: "A clear brook still runs through the village, as it always has." },
    { src: "/forste/forest1.jpg", caption: "The wooded slopes above Förste that hold the Lichtenstein Cave." },
    { src: "/forste/forest3.jpg", caption: "Sunlight in the limestone ravine near the cave." },
    { src: "/forste/creek-bw.jpg", caption: "Village children at play by the creek, mid-20th century." },
    { src: "/forste/lake.jpg", caption: "Geese on the village pond on a still winter's day." },
  ],
};

export const timeline = {
  eyebrow: "By Forest and by Stone",
  title: "One line, three thousand years long.",
  intro:
    "From a face rebuilt from a Bronze-Age skull to the family living in Förste today — the same blood, generation after generation.",
  entries: [
    {
      id: "ancestor",
      name: "The Ancestors",
      year: "c. 1000 BC",
      fan: true,
      faces: [
        { id: "matriarch", name: "The Matriarch", img: "/people/matriarch.jpg" },
        { id: "ancestor", name: "The Ancestor", img: "/people/ancestor.jpg" },
        { id: "kinswoman", name: "The Kinswoman", img: "/people/kinswoman.jpg" },
      ],
    },
    { id: "walter", img: "/people/walter.jpg", name: "Walter", year: "1904" },
    { id: "liselot", img: "/people/liselot.jpg", name: "Liselot", year: "1927" },
    { id: "peter", img: "/people/peter.jpg", name: "Peter", year: "1939" },
    { id: "manfred", img: "/people/manfred.jpg", name: "Manfred", year: "1950" },
    { id: "david", img: "/people/david.jpg", name: "David", year: "1951" },
    { id: "volker", img: "/people/volker1.jpg", name: "Volker", year: "1989" },
  ],
};

export const distribution = {
  label: "Distribution",
  title: "A rare and ancient name.",
  intro:
    "Where the great clans are counted in the hundreds of thousands, the Huchthausens are few — one of the rarest surnames on earth, and perhaps the oldest of all.",
  bigStat: { value: "≈ 500", label: "Huchthausens worldwide" },
  bigNote:
    "Counted in hundreds, not thousands — a name carried by a single, deeply-rooted kindred.",
  stats: [
    { value: "3,000", label: "years in one place — the longest-documented family line known" },
    { value: "122", label: "generations of unbroken descent" },
    { value: "No. 1", label: "oldest proven family continuity on earth" },
  ],
  places: [
    { place: "Germany (Harz heartland)", value: "≈ 320" },
    { place: "United States", value: "≈ 90" },
    { place: "Canada", value: "≈ 20" },
    { place: "Netherlands", value: "≈ 15" },
    { place: "Elsewhere", value: "≈ 55" },
  ],
  note:
    "Estimates drawn from public surname records (Forebears) and press coverage of the Lichtenstein Cave DNA study.",
};

export const bloodline = {
  label: "The Bloodline",
  title: "Are you one of us?",
  text:
    "Few carry the name Huchthausen — and its cousins Huchtmann, Huchting and Huchtkötter. If the thicket runs in your blood, take your place among the oldest kindred on earth.",
  cta: "Trace Your Name",
};

export const gathering = {
  label: "The Gathering",
  title: "Join the Kindred.",
  text:
    "Receive the family chronicle, gathering invitations and lineage research direct from the Harz.",
  cta: "Pledge Your Name",
};

export const kindred = {
  label: "The Kindred",
  title: "The living line.",
  text: "Every name pledged joins a roll that grows with each gathering.",
};

export const footer = {
  cols: [
    { title: "The Family", links: ["Origins", "The Name", "The Kin", "Lineage"] },
    { title: "Heritage", links: ["Legacy", "The Roots", "The World", "The Cave"] },
    { title: "The Gathering", links: ["Join the Kindred", "The Chronicle", "Lineage Research", "Contact"] },
  ],
  legal: ["Imprint", "Privacy", "Cookies"],
  note:
    "A heritage site devoted to the Huchthausen family. Historical content is drawn from public sources about the family and the Lichtenstein Cave DNA study; population figures are illustrative estimates.",
};


// Real, public press coverage of the Huchthausen family & Lichtenstein Cave.
// We store outlet, region, a short factual headline, our own summary and a
// short attributed quote, plus a link to the original (opened in a new tab).
export const press = {
  label: "The Press",
  title: "The family in the news.",
  intro:
    "For nearly two decades the Huchthausens and the Lichtenstein Cave have drawn reporters from across Germany and the world. Below is a chronicle of that coverage — with a short quotation from each and a link to read the original in full.",
  legalNote:
    "Out of respect for each publisher's copyright, we do not reproduce their pages or images here. Each entry uses only a brief quotation for the purpose of reference and criticism, with full attribution and a link to the original article. All rights remain with the respective publications.",
  clippings: [
    {
      id: "pr1",
      outlet: "NDR / Tagesschau",
      region: "Germany",
      lang: "DE",
      date: "2023",
      headline: "Eine Familie ist seit 122 Generationen im Harz verwurzelt",
      summary:
        "Germany's public broadcaster reports how DNA analysis tied the Huchthausen family to Bronze-Age remains, tracing 122 generations rooted in one Harz region.",
      quote: "seit 122 Generationen im Harz verwurzelt",
      url: "https://www.tagesschau.de/inland/regional/niedersachsen/eine-familie-ist-seit-122-generationen-im-harz-verwurzelt,huchthausen-100.html",
    },
    {
      id: "pr2",
      outlet: "Der Spiegel",
      region: "Germany",
      lang: "DE",
      date: "2007",
      headline: "Verwandt mit Höhlenmenschen: Eine Stadt sucht ihren Ur-Opa",
      summary:
        "Spiegel tells the story of a Harz town giving saliva samples to find out who descends from the cave's Bronze-Age dead — a hunt for a shared great-grandfather.",
      quote: "Eine Stadt sucht ihren Ur-Opa",
      url: "https://www.spiegel.de/wissenschaft/mensch/verwandt-mit-hoehlenmenschen-eine-stadt-sucht-ihren-ur-opa-a-465685.html",
    },
    {
      id: "pr3",
      outlet: "ABC News",
      region: "United States",
      lang: "EN",
      date: "2008",
      headline: "German men trace ancestry 3,000 years to a Bronze-Age cave",
      summary:
        "ABC News covered the discovery internationally, describing what reporters called the world's longest proven family tree, reaching back roughly 120 generations.",
      quote: "the world's longest proven family tree",
      url: "https://abcnews.com/International/story?id=5386347&page=1",
    },
    {
      id: "pr4",
      outlet: "Cicero",
      region: "Germany",
      lang: "DE",
      date: "2008",
      headline: "Seit 3000 Jahren nicht umgezogen",
      summary:
        "Cicero reflects on a family that, in journalistic shorthand, has 'not moved house' in three thousand years — and what such deep rootedness means.",
      quote: "Seit 3000 Jahren nicht umgezogen",
      url: "https://www.cicero.de/kultur/3000-jahren-nicht-umgezogen/47627",
    },
    {
      id: "pr5",
      outlet: "Die Welt",
      region: "Germany",
      lang: "DE",
      date: "2008",
      headline: "Die Verwandten aus der Bronzezeit",
      summary:
        "Welt am Sonntag profiles the living descendants — among them Manfred Huchthausen — and their newly proven relatives from the Bronze Age.",
      quote: "Die Verwandten aus der Bronzezeit",
      url: "https://www.welt.de/wams_print/article2208000/Die-Verwandten-aus-der-Bronzezeit.html",
    },
    {
      id: "pr6",
      outlet: "Tagesspiegel",
      region: "Germany",
      lang: "DE",
      date: "2007",
      headline: "In 120. Generation",
      summary:
        "Der Tagesspiegel examines the science behind the claim, explaining how researchers matched ancient DNA to residents living near the cave today.",
      quote: "In 120. Generation",
      url: "https://www.tagesspiegel.de/wissen/in-120-generation-1674361.html",
    },
    {
      id: "pr7",
      outlet: "Focus",
      region: "Germany",
      lang: "DE",
      date: "2025",
      headline: "Familie lebt seit 3000 Jahren am gleichen Ort",
      summary:
        "Focus revisits the story, noting that the daughters of the family represent the 122nd generation to live in the same place.",
      quote: "Töchter sind 122. Generation",
      url: "https://www.focus.de/panorama/welt/toechter-sind-122-generation-familie-lebt-seit-3000-jahren-am-gleichen-ort_29aed651-7c19-4b3e-8cbf-d8bc662833b6.html",
    },
    {
      id: "pr8",
      outlet: "NOZ",
      region: "Germany",
      lang: "DE",
      date: "2023",
      headline: "Seit 3000 Jahren leben die Huchthausens am gleichen Ort",
      summary:
        "The Neue Osnabrücker Zeitung recounts the local pride around a family whose roots in the Harz reach back three millennia.",
      quote: "Seit 3000 Jahren … am gleichen Ort",
      url: "https://www.noz.de/deutschland-welt/panorama/artikel/seit-3000-jahren-leben-die-huchthausens-am-gleichen-ort-22647760",
    },
    {
      id: "pr9",
      outlet: "BILD",
      region: "Germany",
      lang: "DE",
      date: "2025",
      headline: "„Meine Familie lebt seit 3000 Jahren in unserem Dorf“",
      summary:
        "BILD gives the story a personal voice, quoting a family member on what it feels like to belong to one village for three thousand years.",
      quote: "Meine Familie lebt seit 3000 Jahren in unserem Dorf",
      url: "https://www.bild.de/regional/niedersachsen/dna-fund-meine-familie-lebt-seit-3000-jahren-in-unserem-dorf-68e61d99baad99385c954088",
    },
    {
      id: "pr10",
      outlet: "Colombia One",
      region: "International",
      lang: "EN",
      date: "2025",
      headline: "A German family has lived in the same village for generations",
      summary:
        "An English-language feature that carried the Harz story to international readers, framing it as one of the most remarkable cases of ancestral continuity on record.",
      quote: "the same village for generations",
      url: "https://colombiaone.com/2025/11/14/german-family-lived-same-village-generations/",
    },
    {
      id: "pr11",
      outlet: "Frankfurter Rundschau",
      region: "Germany",
      lang: "DE",
      date: "2007",
      headline: "Mein Ur-Opa aus der Höhle",
      summary:
        "The Frankfurter Rundschau captures the wonder of ordinary people discovering a great-grandfather among Bronze-Age cave remains.",
      quote: "Mein Ur-Opa aus der Höhle",
      url: "https://www.fr.de/panorama/mein-ur-opa-hoehle-11610720.html",
    },
    {
      id: "pr12",
      outlet: "Spektrum der Wissenschaft",
      region: "Germany",
      lang: "DE",
      date: "2010",
      headline: "Die Menschen aus der Lichtensteinhöhle",
      summary:
        "A science magazine deep-dive into the archaeology and genetics of the cave, the source of the whole extraordinary family story.",
      quote: "Die Menschen aus der Lichtensteinhöhle",
      url: "https://www.spektrum.de/magazin/die-menschen-aus-der-lichtensteinhoehle/1039641",
    },
    {
      id: "pr13",
      outlet: "Kreiszeitung",
      region: "Germany",
      lang: "DE",
      date: "2025",
      headline: "Fund in der Lichtensteinhöhle verbindet den Harz mit der Bronzezeit",
      summary:
        "The regional Kreiszeitung covers the local angle from Osterode, naming Manfred Huchthausen and Uwe Lange among the residents tied by DNA to the cave.",
      quote: "eine der ältesten Familien der Welt",
      url: "https://www.kreiszeitung.de/lokales/niedersachsen/osterode-niedersachsen-fund-lichtsteinhoehle-bronzezeit-familie-huchthausen-lange-92790111.html",
    },
    {
      id: "pr14",
      outlet: "oe24",
      region: "Austria",
      lang: "DE",
      date: "2025",
      headline: "Diese Familie lebt seit 3000 Jahren am selben Ort",
      summary:
        "The Austrian outlet oe24 carried the story to readers beyond Germany, marvelling at a family fixed to one place for three millennia.",
      quote: "seit 3000 Jahren am selben Ort",
      url: "https://www.oe24.at/a/diese-familie-lebt-seit-3000-jahren-am-selben-ort-900110003",
    },
    {
      id: "pr15",
      outlet: "MSN",
      region: "International",
      lang: "DE",
      date: "2025",
      headline: "DNA-Spuren zeigen: Familie lebt seit 3000 Jahren im gleichen Dorf",
      summary:
        "A widely syndicated feature on MSN summarised the DNA findings for a broad international audience.",
      quote: "seit 3000 Jahren im gleichen deutschen Dorf",
      url: "https://www.msn.com/de-ch/nachrichten/other/dna-spuren-zeigen-familie-lebt-seit-3000-jahren-im-gleichen-deutschen-dorf/ar-AA1Qk9Lx",
    },
    {
      id: "pr16",
      outlet: "Deutsche Welle",
      region: "International",
      lang: "RU",
      date: "2025",
      headline: "Хухтхаузены: семья с самой древней родословной",
      summary:
        "Deutsche Welle's Russian service told the Huchthausen story to readers across the Russian-speaking world.",
      quote: "самая древняя родословная",
      url: "https://www.dw.com/ru/huhthauzeny-gde-zivet-sema-s-samoj-drevnej-rodoslovnoj/a-70750058",
    },
    {
      id: "pr17",
      outlet: "GenealogyInTime",
      region: "International",
      lang: "EN",
      date: "2008",
      headline: "The 3,000-year-old family tree",
      summary:
        "A genealogy magazine explained why the cave match is celebrated as one of the oldest documented family connections on record.",
      quote: "one of the oldest family connections ever documented",
      url: "http://www.genealogyintime.com/news/2008/3000-year-old-family-tree.html",
    },
    {
      id: "pr18",
      outlet: "Wikipedia",
      region: "Reference",
      lang: "EN",
      date: "current",
      headline: "Lichtenstein Cave",
      summary:
        "The encyclopedic reference gathers the archaeology and the DNA study that connect the Bronze-Age burials to living descendants in the Harz.",
      quote: "one of the oldest known family trees",
      url: "https://en.wikipedia.org/wiki/Lichtenstein_Cave",
    },
  ],
};

export const recipes = {
  label: "The Kitchen",
  title: "Tastes of the Harz.",
  intro:
    "Hearty, honest food from the forests and villages of the Harz — the kind of dishes a Huchthausen table has carried through the generations. Original recipes, simply told.",
  note:
    "The Harz dishes are written for this site; the ancient hearth recipes are retold from the museum kitchens and experimental archaeologists cited above — chiefly Monika Bode’s ‘Frau Bode kocht’ at the Landesmuseum für Vorgeschichte in Halle. Quantities are guides — cook by feel, as the old kitchens did.",
  bronzeAge: {
    eyebrow: "The Bronze-Age table",
    heading: "What the clan of the cave ate",
    foodImages: [
      { src: "/recipes/emmer.jpg", caption: "Emmer — one of the first wheats" },
      { src: "/recipes/millet.jpg", caption: "Broomcorn millet, new to the late Bronze Age" },
      { src: "/recipes/beans.jpg", caption: "The broad, or field, bean" },
      { src: "https://images.unsplash.com/photo-1773957949191-7d57d0456544?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000", caption: "Wild honey, to sweeten the pot" },
      { src: "/recipes/flatbread.jpg", caption: "Spelt flatbread from the hearth" },
      { src: "https://images.unsplash.com/photo-1486297678162-eb2a19b0a32d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000", caption: "Cheese and quark from the new dairy" },
      { src: "/recipes/bronze-crust.jpg", caption: "A charred food crust, baked onto a Bronze-Age pot" },
      { src: "/recipes/bronze-grain.jpg", caption: "Barley and wheat — the grains they returned to" },
    ],
    body: [
      "Long before the Harz kitchen of today, the family laid to rest in the Lichtenstein Cave ate the food of the late Bronze Age. Their menu was the end of a long story — one that runs, as the Kiel archaeologists tell it, through three great ‘food revolutions’ of prehistory.",
      "For thousands of years before them, the wandering people of the older Stone Age lived on what they could catch and find: game, fish and wild berries — a true ‘paleo’ diet. Only about six thousand years ago, in the north, did people begin to settle at fixed places, keep animals and sow grain. Professor Wiebke Kirleis of Kiel calls this the first of the three revolutions.",
      "The second, still in the Neolithic, brought the dairy: cheese and quark, and in time fresh milk, as the lactose tolerance first bred in the farming societies of present-day Hungary, Austria and Slovakia spread north. People had learned not merely to gather food, but to transform and keep it.",
      "The third began around 1200 BC, at the turn from the middle to the late Bronze Age, when the larder suddenly widened. Where the early Bronze Age had known only a handful of crops, the choice now grew much greater. To the older staples — emmer, einkorn, barley, peas, lentils and flax — were added broomcorn millet, the broad (field) bean, and gold-of-pleasure (Leindotter): a weed of the flax fields, too sharp for fodder, but whose oil served both the cooking pot and the lamp.",
      "Everyday fare was porridge, soup and flatbread in endless variation — filling and easily made — though the ‘three B’s’, bread, roast and beer, were never neglected, and fine baking was done too. So a late-Bronze-Age table might carry spelt flatbread, millet porridge sweetened with honey, beef and cheese, and perhaps already a beer to wash it down.",
      "Nothing of a slaughtered animal was wasted. From snout to tail the beast was used: meat, blood and marrow for food; offal from heart to brain eaten; hide for clothing, belts and vessels; bone and antler for tools; sinew for bowstrings, binding and thread; and the cleaned, dried bladder for a flask. Food was precious, and the careful use of it secured not just one life but the survival of the whole community.",
      "How is any of this known, from an age that left no writing? ‘Our greatest helper is the mishap,’ the Kiel researchers say with a wink — ‘lucky for us, the Bronze Age had no Teflon pans.’ When milk boiled over or porridge caught, a food crust baked onto the pot; when a house burned, its stored grain was charred and so preserved, its shape still readable under the microscope. Refuse pits, filled quickly and sealed from air and light, kept husks, bones and pollen for three thousand years — and modern biochemistry can read even a faint trace of fat in a crust to tell what once cooked there.",
      "The grains themselves tell of a restless, adaptable people. Millet had only lately reached Europe from north-east China; a Max Planck study of late-Bronze-Age Central Europe finds that these families took it up as it suited them, then set it aside again for wheat and barley — a story of experiment and choice, not of newcomers. Isotopes in their bones show most were locals, their new ideas carried by contact rather than migration, and their burials — cremation, earth-burial, even the burial of skulls alone — as varied as their tastes. Theirs was a hard, physical life, but on the whole a healthy one.",
    ],
    sourcesLabel: "Sources & further reading",
    sources: [
      { label: "Uni Kiel — Speiseplan der Bronzezeit (Prof. W. Kirleis, Dr. J. Kneisel)", url: "https://www.uni-kiel.de/de/unizeit/uz/news/speiseplan-der-bronzezeit" },
      { label: "eMuseum Himmelswege — Frau Bode kocht (Landesmuseum für Vorgeschichte Halle)", url: "https://www.emuseum-himmelswege.de/freiraum/frau-bode-kocht" },
      { label: "PalaFitFood — Hirse-Dinkel-Brei (AG Lebendige Archäologie / Zeiteninsel)", url: "https://palafitfood.com/koch-challenge/hirse-dinkel-brei/" },
      { label: "Max-Planck-Gesellschaft — Leben und Tod in Mitteleuropa in der späten Bronzezeit", url: "https://www.mpg.de/26207972/leben-und-tod-in-mitteleuropa-in-der-spaeten-bronzezeit" },
    ],
  },
  market: {
    eyebrow: "From the family table",
    heading: "Still made in Förste",
    intro:
      "The oldest family on earth still keeps a working larder. From the same Harz village where the line has stood for 122 generations, the Huchthausens make honey and beer by hand — and no Förste table is without the water of the village spring.",
    products: [
      {
        id: "honey",
        name: "Huchthausen Honig",
        maker: "Manfred & family",
        kind: "German flower honey",
        image: "/recipes/honey.jpg",
        text:
          "Creamy blossom honey from the family's own bees in the Harz. Manfred and his family draw it, jar it and label it by hand — a golden thread running straight back to the meadows above the cave.",
        availability: "Available in Förste",
      },
      {
        id: "beer",
        name: "Harzer Craft-Bier",
        maker: "Volker & family",
        kind: "Dunkel & Pale craft beer",
        image: "/recipes/beer.jpg",
        badge: "/recipes/beer-logo.jpg",
        text:
          "Harzer Craft-Bier is a small craft microbrewery in Osterode am Harz, its brewing kit largely designed and built by hand in the founders' own garage. Volker Huchthausen is the brewery's events manager — and chair of the Kulturschmiede Osterode e.V. — the man behind the beer wagon at fairs across the region. Small-batch beers, among them a dark ‘Dunkel’ and a pale, brewed with Harz water and named for the Brocken and the hills.",
        availability: "Available in Förste & across the Harz",
        link: { url: "https://harzer-craft-bier.de/", label: "harzer-craft-bier.de" },
      },
      {
        id: "water",
        name: "Grafenquelle",
        maker: "Harzer Mineralbrunnen · Förste",
        kind: "Mineral spring water · a village heritage",
        image: "/recipes/water.jpg",
        text:
          "The Grafenquelle was Förste's own mineral spring — a well-known Harz Mineralbrunnen bottled in the village itself. The company closed in 2022, so its water is now a piece of local heritage rather than a product on the shelf — but it is the water on which every Huchthausen grew up.",
        availability: "Village heritage · closed 2022",
      },
    ],
  },
  dishes: [
    {
      id: "d1",
      name: "Harzer Käse „mit Musik“",
      tag: "Starter",
      time: "15 min + resting",
      serves: "4",
      image:
        "https://images.unsplash.com/photo-1602406372206-4d58edc0b733?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTgyOTh8MHwxfHNlYXJjaHwxfHxHZXJtYW4lMjBmb29kfGVufDB8fHx8MTc4ODAxNDU1MXww&ixlib=rb-4.1.0&q=85&w=1000",
      intro:
        "The Harz sour-milk cheese, dressed with onions, oil and vinegar — the ‘music’ that makes it sing.",
      ingredients: [
        "4 rounds of Harzer sour-milk cheese",
        "2 onions, finely diced",
        "4 tbsp cider vinegar",
        "3 tbsp oil",
        "1 tsp caraway seeds",
        "Salt, pepper, a little sugar",
      ],
      steps: [
        "Whisk vinegar, oil, caraway, salt, pepper and a pinch of sugar.",
        "Stir in the diced onion and let stand 10 minutes.",
        "Lay the cheese in a shallow dish and pour the dressing over.",
        "Rest at least 1 hour so the ‘music’ soaks in. Serve with dark bread.",
      ],
    },
    {
      id: "d2",
      name: "Harzer Wildgulasch",
      tag: "Main",
      time: "2.5 hours",
      serves: "4–6",
      image: "/recipes/wildgulasch.jpg",
      intro:
        "A dark venison goulash from the Harz forests, slow-braised with juniper and red wine.",
      ingredients: [
        "1 kg venison, cubed",
        "3 onions, chopped",
        "2 carrots, diced",
        "200 ml red wine",
        "500 ml stock",
        "8 juniper berries, 2 bay leaves",
        "2 tbsp flour, oil, salt, pepper",
      ],
      steps: [
        "Brown the venison in oil in batches; set aside.",
        "Soften onions and carrots, dust with flour and stir 1 minute.",
        "Deglaze with red wine, add stock, juniper and bay.",
        "Return the meat, cover and braise gently 2 hours until tender.",
        "Season well and serve with dumplings or bread.",
      ],
    },
    {
      id: "d3",
      name: "Grünkohl mit Bregenwurst",
      tag: "Main",
      time: "1.5 hours",
      serves: "4",
      image: "/recipes/gruenkohl.jpg",
      intro:
        "Winter kale stewed low and long with regional Bregenwurst — a Lower-Saxon classic of the Harz foothills.",
      ingredients: [
        "1.5 kg kale, stripped and chopped",
        "4 Bregenwurst (or smoked sausages)",
        "2 onions, 2 tbsp lard",
        "500 ml stock",
        "1 tbsp mustard, salt, pepper, pinch of oats",
      ],
      steps: [
        "Sweat onions in lard, add kale and let it collapse.",
        "Pour in stock, season, and simmer covered 45 minutes.",
        "Nestle the sausages in and cook a further 30 minutes.",
        "Stir in mustard and a little oats to thicken. Serve with boiled potatoes.",
      ],
    },
    {
      id: "d4",
      name: "Baumkuchen",
      tag: "Cake",
      time: "2 hours",
      serves: "1 cake",
      image: "/recipes/baumkuchen.jpg",
      intro:
        "The ‘tree cake’ — baked layer upon layer so a slice shows rings, like our own three-thousand-year tree.",
      ingredients: [
        "250 g butter, 250 g sugar",
        "6 eggs, separated",
        "200 g flour, 50 g cornflour",
        "1 tsp vanilla, pinch of salt",
        "Apricot jam and dark chocolate to finish",
      ],
      steps: [
        "Cream butter and sugar, beat in yolks and vanilla.",
        "Fold in sifted flours, then the stiffly whipped whites.",
        "Under a hot grill, spread a thin layer in a tin and bake until golden; repeat, layer by layer.",
        "Cool, brush with warmed jam and coat in chocolate. Slice to reveal the rings.",
      ],
    },
    {
      id: "d5",
      name: "Kräuterquark mit Pellkartoffeln",
      tag: "Light",
      time: "35 min",
      serves: "4",
      image: "/recipes/kraeuterquark.jpg",
      intro:
        "Herb quark with jacket potatoes — the simple supper of the thicket, green with garden herbs.",
      ingredients: [
        "800 g small waxy potatoes",
        "500 g quark",
        "100 ml cream or milk",
        "1 bunch mixed herbs (chives, parsley, dill), chopped",
        "1 onion, salt, pepper",
      ],
      steps: [
        "Boil the potatoes in their skins until tender, 20–25 minutes.",
        "Beat quark with cream until smooth and loose.",
        "Fold in herbs and finely diced onion; season boldly.",
        "Serve the hot potatoes split open with a spoonful of green quark.",
      ],
    },
    {
      id: "d6",
      name: "Heidelbeer-Grütze",
      tag: "Dessert",
      time: "25 min",
      serves: "4",
      image: "/recipes/heidelbeer.jpg",
      intro:
        "Warm blueberry compote from the Harz uplands, thickened just so and served with cool cream.",
      ingredients: [
        "600 g blueberries",
        "80 g sugar",
        "250 ml water or apple juice",
        "2 tbsp cornflour",
        "Cream or vanilla sauce to serve",
      ],
      steps: [
        "Warm blueberries with sugar and most of the liquid until they burst.",
        "Slake the cornflour in the reserved cold liquid.",
        "Stir it in and simmer 1–2 minutes until glossy and thick.",
        "Serve warm or cold with a pool of cream.",
      ],
    },
    {
      id: "d7",
      name: "Hirse-Dinkel-Brei mit Haselnussmus",
      tag: "Bronze Age",
      time: "25 min",
      serves: "4",
      image: "/recipes/millet.jpg",
      credit: "Recipe: AG Lebendige Archäologie / Zeiteninsel",
      intro:
        "A millet-and-spelt porridge with hazelnut butter — a genuine Bronze-Age dish, sweetened with honey as the cave clan would have known it.",
      ingredients: [
        "3 level tbsp hulled millet (or millet flakes)",
        "500 ml milk",
        "3 level tbsp spelt semolina (Dinkelgrieß)",
        "For the hazelnut butter: 125 g butter",
        "100 g ground hazelnuts",
        "1½ level tbsp honey",
      ],
      steps: [
        "Bring the millet to the boil in 0.25 l of the milk, then swell over low heat for 15 minutes, stirring constantly (flakes are quicker).",
        "When swollen, stir in about 0.2 l more milk to the thickness you like.",
        "Stir in the spelt semolina and bring back to the boil, stirring throughout — a workout for the wrist.",
        "For the butter: beat the soft butter until creamy; toast the ground hazelnuts in a little butter, turning constantly (they scorch fast).",
        "Fold the warm nuts into the butter, stir in the honey, pot up and cool. Serve the porridge topped with the hazelnut butter.",
      ],
    },
    {
      id: "d8",
      name: "Ackerbohnen im Tontopf",
      tag: "Bronze Age",
      time: "1 hr + soaking",
      serves: "4",
      image: "/recipes/beans.jpg",
      credit: "After ‘Frau Bode kocht’, LDA Sachsen-Anhalt",
      intro:
        "Field (broad) beans slow-cooked in a clay pot — the protein of prehistory, arriving on Central-European tables in the Bronze Age. Good plain, or with a little bacon.",
      ingredients: [
        "400 g dried broad (field) beans, soaked overnight",
        "1 onion, chopped",
        "100 g bacon or speck, diced (optional)",
        "1 bay leaf, a sprig of savory",
        "Salt, and a little honey or vinegar to finish",
      ],
      steps: [
        "Drain the soaked beans and cover with fresh water in an earthenware pot.",
        "Add onion, bacon if using, bay and savory, and bring gently to a simmer.",
        "Cook slowly 45–60 minutes, over embers or low heat, until the beans are soft.",
        "Season with salt; sharpen with a spoon of honey or a splash of vinegar. Serve with flatbread.",
      ],
    },
    {
      id: "d9",
      name: "Dinkel-Fladen mit Kräuterquark",
      tag: "Bronze Age",
      time: "40 min",
      serves: "4",
      image: "/recipes/bread-honey.jpg",
      credit: "After ‘Frau Bode kocht’, LDA Sachsen-Anhalt",
      intro:
        "Spelt flatbread baked on a hot stone, served with fresh herb quark — the everyday bread-and-cheese of the late Bronze Age.",
      ingredients: [
        "300 g spelt flour (plus a little emmer if you have it)",
        "About 180 ml water, a pinch of salt",
        "250 g quark (fresh curd cheese)",
        "1 bunch garden herbs (chives, parsley, wild garlic), chopped",
        "A little milk or cream, salt",
      ],
      steps: [
        "Mix flour, salt and enough water to a soft dough; rest 15 minutes.",
        "Divide and roll or press into thin rounds.",
        "Bake on a very hot dry stone or heavy pan, a couple of minutes each side, until blistered.",
        "Loosen the quark with a little milk, fold through the herbs, season, and spread on the warm flatbread.",
      ],
    },
    {
      id: "d10",
      name: "Chicken in a Clay Mantle",
      tag: "Stone Age",
      time: "1.5 hours + fire",
      serves: "2–4",
      image:
        "https://images.unsplash.com/photo-1516483954662-1bee87353427?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
      intro:
        "A young bird sealed in wet clay and buried in the embers — a Stone-Age way to roast without a pot. Done well, the feathers pull clean away, stuck fast in the baked clay.",
      ingredients: [
        "1 young chicken, duck or wild fowl, drawn and ready",
        "Clay, sand and water for the mantle",
        "1–2 onions and a little garlic",
        "Herbs to taste (thyme, rosemary, sage)",
        "Salt",
        "Clean fist-sized stones (5–10 cm, no limestone) and untreated firewood",
      ],
      steps: [
        "Lay and light the fire well ahead, and let it burn down to a glowing bed of embers.",
        "Fill the bird with its heart and gizzard, the onion, garlic and herbs; salt it inside and out.",
        "Wrap the whole bird in a clay mantle about 1–2 cm thick and lay it in the embers.",
        "Cook in the glowing coals at least 60–90 minutes, by the size of the bird and the thickness of the clay.",
        "Lift it out, knock off the worst of the ash, then slowly crack away the clay — at best the feathers come with it. Serve.",
      ],
      credit:
        "Recipe: Monika Bode · ‘Frau Bode kocht’, Landesmuseum für Vorgeschichte Halle · Photo via Unsplash",
    },
    {
      id: "d11",
      name: "Egg on a Hot Stone",
      tag: "Stone Age",
      time: "10 min + fire",
      serves: "2",
      image:
        "https://images.unsplash.com/photo-1595584354167-9c14a60e9e83?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
      intro:
        "Eggs were a springtime delicacy for hunter-gatherers. Beaten with wild herbs and set on a fire-heated slab, they become a simple omelette — no pan required.",
      ingredients: [
        "Hen’s, quail or duck eggs",
        "A little lard for the stone",
        "Chopped wild herbs",
      ],
      steps: [
        "Heat a flat stone slab over the embers and grease it lightly.",
        "Beat the eggs loosely and fold in the chopped herbs.",
        "Pour onto the hot stone and let them set.",
        "With enough wild herbs, no salt is needed.",
      ],
      credit:
        "Recipe: Monika Bode · ‘Frau Bode kocht’, Landesmuseum für Vorgeschichte Halle · Photo via Unsplash",
    },
    {
      id: "d12",
      name: "Fresh Garden-Leaf Salad",
      tag: "Neolithic",
      time: "15 min",
      serves: "4",
      image:
        "https://images.unsplash.com/photo-1574031491550-35f444917508?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
      intro:
        "A salad conjured from garden and hedgerow — young tree leaves dressed with oil, onion and honey. The younger the leaves, the less bitter and the more savoury.",
      ingredients: [
        "3 handfuls young leaves (lime, beech, apple, blackberry or raspberry; topped up with lamb’s lettuce or rocket)",
        "1 onion, finely chopped",
        "Oil, salt and honey to taste",
        "2 hard-boiled eggs (optional)",
      ],
      steps: [
        "Whisk the oil, chopped onion, salt and honey into a marinade.",
        "Wash the leaves, quarter the eggs over them, and finish with the marinade.",
      ],
      credit:
        "Recipe: Monika Bode · ‘Frau Bode kocht’, Landesmuseum für Vorgeschichte Halle · Photo via Unsplash",
    },
    {
      id: "d13",
      name: "Lentil Stew with Dried Fruit",
      tag: "Neolithic",
      time: "1 hr + soaking",
      serves: "4",
      image:
        "https://images.unsplash.com/photo-1737210236138-bd58c5ce3a2e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
      intro:
        "Prehistory’s protein, sweetened with fruit — lentils simmered in a clay pot over the fire, finished with roasted hazelnuts and a thread of honey.",
      ingredients: [
        "2 cups lentils",
        "2 small handfuls dried fruit (apple, pear or barberries)",
        "A little lard or butter",
        "Hazelnuts, to taste",
        "Honey and salt to finish",
      ],
      steps: [
        "Soak the dried fruit overnight in plenty of water.",
        "Next day, add the lentils; tip the mixture into a clay pot set on the fire.",
        "Top up with water if it cooks down too fast.",
        "Lift the pot from the fire and cover — the residual heat finishes the lentils.",
        "Salt, scatter with chopped roasted hazelnuts, and trickle honey over each plate.",
      ],
      credit:
        "Recipe: Monika Bode · ‘Frau Bode kocht’, Landesmuseum für Vorgeschichte Halle · Photo via Unsplash",
    },
    {
      id: "d14",
      name: "Little Fruit Cakes on Hot Stones",
      tag: "Neolithic",
      time: "45 min + resting",
      serves: "4",
      image:
        "https://images.unsplash.com/photo-1620980776848-84ac10194945?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
      intro:
        "Cake in prehistory? A soured-milk dough baked around a greased pebble, then hollowed and filled with berries and honey.",
      ingredients: [
        "Wheat flour (type 550)",
        "Soured milk",
        "Berries of your choice",
        "Honey",
        "A little fat, plus 10–20 clean pebbles",
      ],
      steps: [
        "Knead flour and soured milk to a smooth dough; keep it warm and covered.",
        "Knead again, then let it rest overnight.",
        "Wash and grease 10–20 pebbles, depending on the amount of dough.",
        "Shape fist-sized balls, flatten a little, and press a greased pebble into the middle of each.",
        "Bake in good heat about 30 minutes.",
        "Remove the stones and fill the hollows with berries, then honey on top.",
      ],
      credit:
        "Recipe: Monika Bode · ‘Frau Bode kocht’, Landesmuseum für Vorgeschichte Halle · Photo via Unsplash",
    },
    {
      id: "d15",
      name: "The Oldest Noodles",
      tag: "Bronze Age",
      time: "40 min + drying",
      serves: "4",
      image:
        "https://images.unsplash.com/photo-1606091484089-089fa86fad4f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
      intro:
        "Noodle-like pasta from the Bronze Age was found at the Niederkaina burial ground in Saxony — billed as ‘the oldest noodle in the world’. Dried, it keeps for a long time and travels well.",
      ingredients: [
        "4 handfuls wheat or spelt flour",
        "4 eggs",
        "Salt",
        "A little water, if needed",
        "A strong broth of bones and root vegetables",
      ],
      steps: [
        "Work the flour, eggs and salt to an elastic dough; form a ball, cover and rest.",
        "Roll into finger-thick ropes, or roll thin and tear into small pieces; let them dry briefly.",
        "Boil the broth from bones and root vegetables (or use instant) and cook the noodles in it.",
        "Season with herbs and salt. Dried thoroughly, the raw pasta keeps for a very long time.",
      ],
      credit:
        "Recipe: Monika Bode · ‘Frau Bode kocht’, Landesmuseum für Vorgeschichte Halle · Photo via Unsplash",
    },
    {
      id: "d16",
      name: "Venison in Rhubarb Leaves from the Fire-Pit",
      tag: "Bronze Age",
      time: "2 hours + fire",
      serves: "4–6",
      image:
        "https://images.unsplash.com/photo-1664339030060-191ccb53b5d2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
      intro:
        "Meat from the cooking pit: venison seasoned with wild herbs, wrapped in large leaves and steamed between fire-heated stones — the festival roast of the ancient hearth.",
      ingredients: [
        "Venison (here roe deer), in portion-sized pieces",
        "Garlic",
        "Wild herbs (ramson, cow parsley, ribwort plantain and/or nettle)",
        "Coarse salt",
        "Large leaves (rhubarb or coltsfoot) for wrapping",
      ],
      steps: [
        "Heat the stones on the fire for at least an hour.",
        "Cut the meat to suit the leaves; season with chopped herbs and plenty of salt, and wrap each piece in a leaf.",
        "Lay the hot stones as a layer in the fire-pit, then the leaf parcels, alternating until all are in.",
        "Cover the pit with branches and foliage (or a leather cloth) to hold the heat.",
        "After about an hour the meat is done — mind the stones, they are still very hot.",
      ],
      credit:
        "Recipe: Monika Bode · ‘Frau Bode kocht’, Landesmuseum für Vorgeschichte Halle · Photo via Unsplash",
    },
  ],
};

export const clanMap = {
  label: "The Map",
  title: "Where the family stands.",
  intro:
    "Four places in the Harz hold the whole story — the village, the old town, the hidden cave and the museum that tells it. Explore the map.",
  locations: [
    { id: "m1", name: "Förste", role: "The family seat", lat: 51.687, lng: 10.183, note: "The Harz village where the Huchthausens have lived since the Bronze Age." },
    { id: "m2", name: "Osterode am Harz", role: "The old town", lat: 51.728, lng: 10.253, note: "The historic town beside the cave — gateway to the family's story." },
    { id: "m3", name: "Lichtenstein Cave", role: "Where it began", lat: 51.66, lng: 10.17, note: "The Bronze-Age burial cave whose DNA proved 122 generations of descent." },
    { id: "m4", name: "HöhlenErlebnisZentrum", role: "Where it is told", lat: 51.8085, lng: 10.236, note: "The Iberger Tropfsteinhöhle museum at Bad Grund, where the finds are shown and the cave rebuilt." },
  ],
};

export const guestbook = {
  label: "Guestbook",
  title: "Leave your mark.",
  intro: "Sign the family guestbook. Every entry is read and approved by the family before it appears here.",
  namePlaceholder: "Your name",
  placePlaceholder: "Where you're from (optional)",
  messagePlaceholder: "Your message to the clan",
  submit: "Sign the guestbook",
  needFields: "Please enter your name and a message.",
  successTitle: "Thank you",
  successDesc: "Your entry has been submitted and now awaits approval.",
  emptyText: "No entries yet — be the first to sign.",
  reviewNote: "New entries appear once approved by the family.",
};

// Photo credits (Wikimedia Commons). Attribution is required by the licences
// and is shown verbatim wherever each image appears.
const CR_AXEL = "Photo: Axel Hindemith · CC BY-SA 3.0 DE · via Wikimedia Commons";
const CR_FRICKE = "Photo: Uwe Fricke · Public domain · via Wikimedia Commons";

export const cave = {
  label: "The Cave",
  eyebrow: "The Lichtenstein Cave",
  title: "Where the bloodline begins.",
  intro:
    "Beneath the Harz, in a narrow cave of gypsum karst, a Bronze-Age family clan laid its dead to rest three thousand years ago. Their bones became the oldest large DNA archive in the world — and the proof that the Huchthausens still live where their story began.",
  heroImage: "/cave/entrance-real.jpg",
  mapFocus: "m3",
  facts: [
    { k: "Discovered", v: "1972" },
    { k: "Length", v: "≈ 135 m" },
    { k: "Period", v: "Late Bronze Age" },
    { k: "In use", v: "c. 1000–700 BC" },
    { k: "Individuals", v: "≈ 60" },
    { k: "Location", v: "Osterode am Harz, Lower Saxony" },
  ],
  sections: [
    {
      id: "discovery",
      heading: "Discovered in 1972, opened in 1980",
      body: [
        "In the spring of 1972, local researchers searching for a hidden passage below the ruined Lichtenstein castle instead broke into an unknown natural cave in the gypsum karst of the Harz. For years it seemed an empty curiosity.",
        "Then, in 1980, cavers forced a blocked crevice at its far end and crawled into five untouched chambers — and into thousands of human and animal bones, bronze ornaments and pottery that had lain sealed under a crust of gypsum sinter for three thousand years.",
      ],
      image: "/cave/entrance-1980.jpg",
    },
    {
      id: "finds",
      heading: "A sealed Bronze-Age chamber",
      body: [
        "Around 4,300 human bone fragments were recovered, together with some hundred bronze objects — ear-, arm- and finger-rings — and fragments of pottery. The metal and ceramics belong to the Urnfield culture of the 10th to 8th centuries BC.",
        "That age normally cremated its dead. Here, uniquely, the bodies were left unburnt and undisturbed, preserved by the cave's cool, even air and its gypsum chemistry — an accident of stone that saved a family's DNA.",
      ],
      image: "/cave/entrance-2013.jpg",
    },
    {
      id: "grave",
      heading: "The family grave, as it was found",
      body: [
        "In the innermost chambers lay the dead of a single family, their bones commingled where they had been laid over generations — skulls, long bones and ribs together beneath a crust of gypsum sinter, exactly as the excavators recorded them.",
        "It is from this jumble of bone that the whole story is read: the kinship traced across three generations, the diet, and the unbroken descent that reaches the living family today.",
      ],
      image: "/cave/grave.jpg",
    },
    {
      id: "speleo",
      heading: "A cave few will ever enter",
      body: [
        "The Lichtenstein is a gypsum-karst solution cave — a former underground streamway of low, cramped chambers threading some 135 metres through the hillside. After the 1972 find beneath the ruined castle, its tight opening had to be widened before researchers could work inside at all.",
        "Because it is so fragile and confined, the cave stays closed to the public and under protection, opened only now and then for special occasions such as the 2016 gathering of Germany's cave and karst researchers. Among its chambers the bones of dozens of people were counted — and it was their DNA that first drew a three-thousand-year-old family across the generations.",
      ],
      image: "/cave/passage.jpg",
    },
    {
      id: "clan",
      heading: "The world's largest Bronze-Age DNA pool",
      body: [
        "The bones form the largest Bronze-Age DNA archive on earth. From them, researchers at the University of Göttingen reconstructed — for the first time anywhere — the kinship of a prehistoric community purely from its genes.",
        "Among some sixty individuals, parents and children could be traced across three generations: a single extended family clan, buried together for a century or two around 1000 BC.",
      ],
      image: "/cave/hill.jpg",
    },
    {
      id: "descendants",
      heading: "A saliva sample that closed the circle",
      body: [
        "In 2007 the villages within sight of the Lichtenstein were asked to give saliva samples. Two men still living there carried an extremely rare genetic lineage — identical to that of a man buried in the cave.",
        "Across more than a hundred generations, the same blood had never left the valley. It is the discovery that gives the Huchthausens their claim: the oldest documented family continuity on earth.",
      ],
      image: "/people/manfred3.jpg",
      credit: "Manfred Huchthausen · Family photograph",
    },
    {
      id: "visiting",
      heading: "Closed to the public, rebuilt for all",
      body: [
        "The cave itself is sealed to protect what remains. A thirteen-metre reconstruction can be walked — and partly crawled — at the HöhlenErlebnisZentrum of the Iberger Tropfsteinhöhle near Bad Grund, about eleven kilometres away, where many of the finds are on show at the Museum am Berg.",
      ],
    },
  ],
  connection: {
    heading: "From this dark, our name.",
    body:
      "Every branch of the family tree on this site reaches back to the people laid to rest in these chambers. To the Huchthausens the Lichtenstein Cave is not a museum piece — it is the first room of the family home.",
  },
  galleryTitle: "The cave in photographs",
  gallery: [
    { src: "/cave/entrance-real.jpg", caption: "The doorway into the Lichtenstein Cave, cut into the hillside above Förste." },
    { src: "/cave/entrance-winter.jpg", caption: "The cave entrance at the foot of the Lichtenstein, in winter." },
    { src: "/cave/grave.jpg", caption: "The Bronze-Age family grave inside the cave, during excavation." },
    { src: "/cave/entrance-2013.jpg", caption: "The research entrance to the archaeological part of the cave." },
    { src: "/cave/entrance-1980.jpg", caption: "The cave's steel-doored entrance, around 1980." },
    { src: "/cave/hill.jpg", caption: "The Lichtenstein hill above Förste, which holds the cave." },
    { src: "/forste/aerial.jpg", caption: "The village of Förste from above, beneath the wooded hills that hold the cave." },
    { src: "/cave/infoboard.jpg", caption: "The information board at the site, telling the story of the Bronze-Age family grave." },
    { src: "/cave/interior1.jpg", caption: "The rocky, wooded karst of the Lichtenstein hill." },
    { src: "/cave/finds.jpg", caption: "Iron-rich, sinter-stained rock at the site." },
    { src: "/cave/formations.jpg", caption: "The gypsum-karst country around the cave." },
    { src: "/cave/excavation.jpg", caption: "Quarried gypsum karst near the Lichtenstein." },
    { src: "/cave/chamber.jpg", caption: "The path through the woods up to the cave." },
    { src: "/cave/passage.jpg", caption: "A wet, narrow passage deep inside the cave." },
    { src: "/cave/gallery-entrance-woods.jpg", caption: "A dark cave mouth in the wooded karst of the Harz." },
    { src: "/cave/gallery-crag.jpg", caption: "A limestone crag rising from the Harz woods above Förste." },
    { src: "/cave/gallery-karst-wall.jpg", caption: "The gypsum-karst rock of the hills that hold the cave." },
    { src: "/cave/gallery-ravine.jpg", caption: "The limestone ravine in the woods above the village." },
    { src: "/cave/gallery-harz-valley.jpg", caption: "The Harz valley beneath the Lichtenstein, in autumn." },
    { src: "/cave/gallery-thicket.jpg", caption: "The dense Harz thicket — the 'Hucht' of the family name." },
    { src: "/cave/gallery-mossy.jpg", caption: "Moss-covered karst boulders in the forest above the cave." },
    { src: "/cave/gallery-path.jpg", caption: "The path through the woods toward the Lichtenstein." },
    { src: "/cave/gallery-autumn.jpg", caption: "Autumn colour in the forest that hides the cave." },
  ],
  creditsTitle: "Sources",
  legalNote:
    "The text on this page is adapted from the German and English Wikipedia articles on the Lichtenstein Cave (Lichtensteinhöhle), available under the Creative Commons Attribution-ShareAlike licence.",
  sourcesLabel: "Further reading",
  sources: [
    { label: "Wikipedia — Lichtenstein Cave (EN)", url: "https://en.wikipedia.org/wiki/Lichtenstein_Cave" },
    { label: "Wikipedia — Lichtensteinhöhle (DE)", url: "https://de.wikipedia.org/wiki/Lichtensteinh%C3%B6hle" },
  ],
};

export const notable = {
  label: "Notable Kin",
  eyebrow: "Notable Huchthausens",
  title: "Names the wider world came to know.",
  intro:
    "From a lineage counted in mere hundreds, a remarkable few carried the name into scholarship, art, war and the sea. Here are the Huchthausens that history has recorded.",
  people: [
    {
      id: "ancestor",
      born: -1002,
      name: "The Bronze-Age Ancestor",
      epithet: "The First",
      era: "c. 1000 – 700 BC",
      place: "Lichtenstein Cave · Harz",
      field: "Founder of the line",
      photo: "/people/ancestor.jpg",
      photos: ["/people/ancestor.jpg", "/people/ancestor2.jpg"],
      bio:
        "The face at the head of the family — a forensic reconstruction built from the skull of one of the men laid to rest in the Lichtenstein Cave some three thousand years ago. It is his DNA that runs, unbroken, to the living Huchthausens of Förste today. The bust stands at the centre of the exhibition on the Bronze-Age clan buried in the cave, about fifteen kilometres away.",
    },
    {
      id: "manfred",
      born: 1950,
      name: "Manfred Huchthausen",
      epithet: "The Living Proof",
      era: "Born 1950",
      place: "Förste · Harz",
      field: "Living descendant",
      photo: "/people/manfred.jpg",
      photos: ["/people/manfred.jpg", "/people/manfred2.jpg", "/people/manfred3.jpg"],
      bio:
        "A man from Förste whose DNA closed a three-thousand-year circle. Tested against the Bronze-Age dead of the nearby Lichtenstein Cave, his genetic lineage matched theirs exactly — evidence that his family has lived within sight of the same hills for some 122 generations, the longest documented continuity of any family on earth.",
    },
    {
      id: "liselot",
      born: 1927,
      name: "Liselot Huchthausen",
      epithet: "The Latinist",
      era: "1927 – 2020",
      place: "Rostock · Germany",
      field: "Classical philologist",
      photo: "/people/liselot.jpg",
      photos: ["/people/liselot.jpg", "/people/liselot2.jpg"],
      bio:
        "A German classical philologist and ancient historian at the University of Rostock, where she held the chair of ancient history. A celebrated editor and translator of Ovid, Cicero and the sources of Roman law, she also pioneered studies of women's lives in the Roman imperial age.",
    },
    {
      id: "peter",
      born: 1939,
      name: "Peter A. Huchthausen",
      epithet: "The Seaman",
      era: "1939 – 2008",
      place: "United States",
      field: "Naval officer & author",
      photo: "/people/peter.jpg",
      photos: ["/people/peter.jpg", "/people/peter2.jpg"],
      bio:
        "A captain of the U.S. Navy, Vietnam veteran and naval attaché in Moscow who became a widely read author of the Cold War at sea. His books — among them Hostile Waters, October Fury and K-19: The Widowmaker — carried the name across the world's oceans and into print.",
    },
    {
      id: "walter",
      born: 1904,
      name: "Walter J. Huchthausen",
      epithet: "The Keeper",
      era: "1904 – 1945",
      place: "United States / Germany",
      field: "Architect & Monuments Man",
      photo: "/people/walter.jpg",
      photos: ["/people/walter.jpg", "/people/walter-civ.jpg"],
      bio:
        "An architect and 'Monuments Man' — a US Army officer charged with protecting Europe's art and monuments in the last months of the Second World War. He was killed in action in Germany while on that mission, and was awarded the Purple Heart.",
    },
    {
      id: "david",
      born: 1951,
      name: "David Huchthausen",
      epithet: "The Glassmaker",
      era: "born 1951",
      place: "Seattle · USA",
      field: "Studio-glass artist",
      photo: "/people/david.jpg",
      photos: ["/people/david.jpg", "/people/david2.jpg"],
      bio:
        "An American artist in glass whose luminous sculptures helped define the studio-glass movement. His work is held in major museums and collections around the world, and he has taught and exhibited across the United States and abroad.",
      link: { url: "http://huchthausen.com/", label: "huchthausen.com" },
    },
    {
      id: "volker",
      born: 1989,
      name: "Volker Huchthausen",
      epithet: "The Next Generation",
      era: "Born 1989",
      place: "Förste · Harz",
      field: "Living descendant · culture organiser",
      photo: "/people/volker2.jpg",
      photos: ["/people/volker2.jpg", "/people/volker1.jpg"],
      bio:
        "Son of Manfred and the next link in the oldest bloodline on earth. Volker carries the Huchthausen line — traced by DNA to the Bronze-Age dead of the Lichtenstein Cave — onward from the Harz village where the family has lived for some 122 generations. Away from the family story he chairs the Kulturschmiede Osterode e.V., a volunteer society founded in 2014 to strengthen music and culture in Osterode am Harz and give young people a stage — from the 'Heavy Christmas' concerts in Förste to open-air meetings across the region.",
      link: { url: "https://kulturschmiede-osterode.de/", label: "kulturschmiede-osterode.de" },
    },
    {
      id: "kinswoman",
      born: -998,
      name: "The Kinswoman",
      epithet: "The Bronze-Age Daughter",
      era: "c. 1000 – 700 BC",
      place: "Lichtenstein Cave · Harz",
      field: "Daughter of the line",
      photo: "/people/kinswoman.jpg",
      photos: ["/people/kinswoman.jpg", "/people/kinswoman2.jpg"],
      bio:
        "A young woman of the cave clan, her face rebuilt from a Bronze-Age skull found among the family laid to rest in the Lichtenstein Cave. She wore her hair in a long plait, as the reconstruction shows — one of the daughters whose blood, carried down 122 generations, still runs in the living Huchthausens of Förste.",
    },
    {
      id: "matriarch",
      born: -1000,
      name: "The Matriarch",
      epithet: "The Bronze-Age Mother",
      era: "c. 1000 – 700 BC",
      place: "Lichtenstein Cave · Harz",
      field: "Mother of the line",
      photo: "/people/matriarch.jpg",
      photos: ["/people/matriarch.jpg", "/people/matriarch2.jpg"],
      bio:
        "An older woman of the same Bronze-Age clan, her weathered face reconstructed from remains sealed in the Lichtenstein Cave for three thousand years. Mother and grandmother of the line, she is among the some sixty souls whose kinship — traced from their genes at Göttingen — first revealed a single family buried together around 1000 BC.",
    },
  ],
  note:
    "Biographies are drawn from public sources, encyclopaedias and the family itself.",
};

const CR_BREY = "Photo: Siegbert Brey · CC BY-SA 4.0 · via Wikimedia Commons";
const CR_AAGN = "Photo: Aagnverglaser · CC BY-SA 4.0 · via Wikimedia Commons";
const CR_LIPPERT = "Photo: Rainer Lippert · CC0 · via Wikimedia Commons";
const CR_AXEL3 = "Photo: Axel Hindemith · CC BY 3.0 · via Wikimedia Commons";

export const hez = {
  label: "HEZ",
  eyebrow: "HöhlenErlebnisZentrum",
  title: "The house that keeps the family.",
  intro:
    "Eleven kilometres from the Lichtenstein Cave, inside a 385-million-year-old coral reef at Bad Grund, the Iberger Tropfsteinhöhle became the HöhlenErlebnisZentrum (HEZ) — the show cave and museum where the story of the world's oldest family is told, and where the Lichtenstein Cave itself is faithfully rebuilt.",
  heroImage: "/hez/interior.jpg",
  mapFocus: "m4",
  facts: [
    { k: "Show cave since", v: "1874" },
    { k: "First described", v: "1723" },
    { k: "Show length", v: "≈ 300 m" },
    { k: "Reef age", v: "≈ 385 million yrs" },
    { k: "Visitors / yr", v: "≈ 67,000" },
    { k: "Location", v: "Bad Grund, Harz" },
  ],
  sections: [
    {
      id: "reef",
      heading: "A reef that travelled the world",
      body: [
        "The Iberg is a geological rarity: the limestone of a Devonian coral reef that first grew some 385 million years ago in a warm sea near where Madagascar lies today, then drifted across the globe to the Harz.",
        "Its caves are rarer still — 'weathering caves', dissolved not by surface water but by acid released as iron ore rusted within the rock. The dripstones, tinted red by iron, formed only after the Harz rose and the flooded galleries ran dry.",
      ],
      image: "/hez/interior.jpg",
    },
    {
      id: "showcave",
      heading: "A show cave since 1874",
      body: [
        "Miners hunting brown ironstone probably found the cave around 1500; the physician Franz Ernst Brückmann left the first account of a descent in 1723. Paths and stairs were laid in 1874, and electric light followed in 1912.",
        "For generations it has been one of the Harz's great attractions — home, to children, of the dwarf-king Hübich, who is said to live beneath the mountain with his people.",
      ],
      image: "/hez/museum.jpg",
    },
    {
      id: "hez",
      heading: "The HöhlenErlebnisZentrum",
      body: [
        "Between 2006 and 2008 the old show cave was rebuilt as the HöhlenErlebnisZentrum, entered through a cube-shaped 'Museum am Berg' beside the road. A new 160-metre tunnel — the 'Museum im Berg', themed 'A Reef on its Travels' — leads into the mountain and the dripstone cave beyond.",
        "Three great stories are told here: 'Fascination Cave', 'A Reef on its Travels', and 'The oldest family in the world'.",
      ],
      image: "/hez/hez.jpg",
    },
    {
      id: "lichtenstein",
      heading: "Where the Lichtenstein Cave is reborn",
      body: [
        "The 'Museum am Berg' is devoted to the nearby Lichtenstein Cave and the Bronze-Age clan from which the Huchthausens descend. Nearly all of the cave's finds are shown here — bronze rings, bangles and pendants, beads, amulets, tools and arrowheads.",
        "Because the real cave is too narrow to enter, a thirteen-metre stretch has been rebuilt true to life, to be walked and crawled. A father, mother and daughter from the clan have even been reconstructed in face and form, from their skulls and DNA.",
      ],
      image: "/hez/exit.jpg",
    },
    {
      id: "visiting",
      heading: "Visiting",
      body: [
        "The HEZ stands on the Harzhochstraße (B242) above Bad Grund, with parking at the door and a bus link from Osterode. A 2023 extension enlarged the museum to show the latest finds — now 57 individuals across yet more generations of the great family.",
      ],
    },
  ],
  connection: {
    heading: "The room where our story is told.",
    body:
      "If the Lichtenstein Cave is the family's first room, the HEZ is where the family is introduced to the world. Here the bones become faces, the DNA becomes a tree, and three thousand years of the Huchthausen line are laid out for every visitor to see.",
  },
  galleryTitle: "The HEZ in photographs",
  gallery: [
    { src: "/hez/interior.jpg", caption: "Dripstones in the Iberger Tropfsteinhöhle." },
    { src: "/hez/museum.jpg", caption: "The 'Museum am Berg' building at the cave entrance." },
    { src: "/hez/hez.jpg", caption: "The HöhlenErlebnisZentrum at Bad Grund." },
    { src: "/hez/old-entrance.jpg", caption: "The former cave entrance, used until 2008." },
    { src: "/hez/exit.jpg", caption: "Inside the show cave, near the exit." },
    { src: "/hez/gallery-building.jpg", caption: "The HöhlenErlebnisZentrum, entered through the cube-shaped 'Museum am Berg'." },
    { src: "/hez/gallery-dripstones.jpg", caption: "Iron-tinted dripstone formations in the Iberg cave." },
    { src: "/hez/gallery-stalactite.jpg", caption: "A single stalactite catching the light deep in the cave." },
    { src: "/hez/gallery-curtain.jpg", caption: "A pale curtain of sinter hanging from the cave roof." },
    { src: "/hez/gallery-dripstone-warm.jpg", caption: "Warm light on the red-tinged dripstones of the Iberg." },
    { src: "/hez/gallery-column.jpg", caption: "A dripstone column lit in the galleries of the show cave." },
    { src: "/hez/gallery-showcave.jpg", caption: "The lit passages of the Iberg show cave." },
    { src: "/hez/gallery-showcave-blue.jpg", caption: "Cool light in the depths of the dripstone cave." },
    { src: "/hez/gallery-popcorn.jpg", caption: "Coralloid 'cave popcorn' on the ancient reef rock." },
    { src: "/hez/gallery-trilobite1.jpg", caption: "A trilobite — a creature of the 385-million-year-old reef that became the Iberg." },
    { src: "/hez/gallery-trilobite2.jpg", caption: "Fossil of the warm sea in which the Iberg reef first grew." },
    { src: "/hez/gallery-fossil.jpg", caption: "A fossil locked in the reef limestone of the Iberg." },
    { src: "/hez/gallery-skull.jpg", caption: "A skull of the Bronze-Age clan, shown at the Museum am Berg." },
    { src: "/hez/gallery-jewellery.jpg", caption: "Bronze-Age beads, rings and ornaments recovered from the Lichtenstein Cave." },
    { src: "/hez/gallery-beads.jpg", caption: "A restrung necklace of beads from the cave burials." },
    { src: "/hez/gallery-museum.jpg", caption: "Inside the museum, where the family's story is told." },
    { src: "/hez/gallery-exhibits.jpg", caption: "Finds and exhibits on show at the HEZ." },
  ],
  creditsTitle: "Sources",
  legalNote:
    "The text on this page is adapted from the German and English Wikipedia articles on the Iberg Dripstone Cave (Iberger Tropfsteinhöhle), available under the Creative Commons Attribution-ShareAlike licence.",
  sourcesLabel: "Further reading",
  sources: [
    { label: "Wikipedia — Iberg Dripstone Cave (EN)", url: "https://en.wikipedia.org/wiki/Iberg_Dripstone_Cave" },
    { label: "Wikipedia — Iberger Tropfsteinhöhle (DE)", url: "https://de.wikipedia.org/wiki/Iberger_Tropfsteinh%C3%B6hle" },
    { label: "HöhlenErlebnisZentrum — official site", url: "https://www.hoehlen-erlebnis-zentrum.de/" },
  ],
};

const CR_NEBRA = "Nebra Sky Disc · Photo: anagoria · Public domain · via Wikimedia Commons";
const CR_UNETICE = "Únětice-culture grave goods · Photo: W. Sarnowska · Public domain · via Wikimedia Commons";

export const bronzeAgeWorld = {
  eyebrow: "The Bronze-Age world",
  title: "A family, and the world around it.",
  intro:
    "The Huchthausen story sits inside a far larger one — the pre- and early history of Central Europe, when metal first reshaped how people lived, traded, worshipped and were buried.",
  cards: [
    {
      image: "/world/unetice.jpg",
      heading: "The Únětice culture",
      body:
        "A thousand years before the cave clan, the Early Bronze Age of Central Europe belonged to the Únětice (Aunjetitz) culture, roughly 2300–1600 BC. Spanning Bohemia, central Germany and beyond, it worked bronze into rings, daggers and ingots, raised princely burial mounds, and laid the trade routes along which metal — and people — moved.",
    },
    {
      image: "/world/nebra.jpg",
      heading: "Sky, metal and the dead",
      body:
        "From this world comes the Nebra Sky Disc — the oldest known image of the cosmos — buried just south of the Harz. By the late Bronze Age of the Lichtenstein Cave, the Urnfield culture usually burned its dead; the sealed, unburnt burials of the Huchthausen ancestors are what let modern science, at the Max Planck institutes and beyond, read life and death in Bronze-Age Central Europe straight from their DNA.",
    },
  ],
  note:
    "Text adapted from Wikipedia (Pre- and early history of Central Europe; Únětice culture) and the Max Planck Society.",
  sourcesLabel: "Sources",
  sources: [
    { label: "Wikipedia — Únětice culture (EN)", url: "https://en.wikipedia.org/wiki/%C3%9An%C4%9Btice_culture" },
    { label: "Wikipedia — Aunjetitzer Kultur (DE)", url: "https://de.wikipedia.org/wiki/Aunjetitzer_Kultur" },
    { label: "Wikipedia — Ur- und Frühgeschichte Mitteleuropas (DE)", url: "https://de.wikipedia.org/wiki/Ur-_und_Fr%C3%BChgeschichte_Mitteleuropas" },
    { label: "Max-Planck-Gesellschaft — late Bronze-Age Central Europe", url: "https://www.mpg.de/26207972/leben-und-tod-in-mitteleuropa-in-der-spaeten-bronzezeit" },
  ],
};
